import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { calorieAdherenceAPI } from '../../services/api'

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

const STATUS_COLORS = {
  on_target: 'bg-green-50 text-green-700',
  surplus: 'bg-red-50 text-red-700',
  deficit: 'bg-green-50 text-green-700',
}

function getMonday(d) {
  const date = new Date(d)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(date.setDate(diff))
}

function formatDate(d) {
  return d.toISOString().split('T')[0]
}

export default function WeeklyComparisonGrid() {
  const { t } = useTranslation()
  const [weekOffset, setWeekOffset] = useState(0)

  const { startDate, endDate } = useMemo(() => {
    const monday = getMonday(new Date())
    monday.setDate(monday.getDate() + weekOffset * 7)
    const sunday = new Date(monday)
    sunday.setDate(sunday.getDate() + 6)
    return { startDate: formatDate(monday), endDate: formatDate(sunday) }
  }, [weekOffset])

  const { data, isLoading } = useQuery({
    queryKey: ['weeklyComparison', startDate, endDate],
    queryFn: () => calorieAdherenceAPI.getAdherence({ start_date: startDate, end_date: endDate }),
    select: (res) => res.data,
  })

  const days = useMemo(() => data?.days || [], [data])
  const summary = data?.summary

  // Build a map: day-of-week index (0=Mon) → day data
  const dayMap = useMemo(() => {
    const map = {}
    days.forEach((d) => {
      const date = new Date(d.date + 'T00:00:00')
      const dow = (date.getDay() + 6) % 7 // 0=Mon
      map[dow] = d
    })
    return map
  }, [days])

  // Compute weekly totals
  const weekTotals = useMemo(() => {
    const result = {
      calories_consumed: 0, calories_burned: 0, target_calories: 0,
      protein_consumed: 0, carbs_consumed: 0, fat_consumed: 0,
      target_protein: 0, target_carbs: 0, target_fat: 0,
      daysWithData: 0,
    }
    days.forEach((d) => {
      if (d.calories_consumed != null) {
        result.calories_consumed += d.calories_consumed
        result.protein_consumed += d.protein_consumed_g || 0
        result.carbs_consumed += d.carbs_consumed_g || 0
        result.fat_consumed += d.fat_consumed_g || 0
        result.daysWithData++
      }
      if (d.calories_burned != null) result.calories_burned += d.calories_burned
      if (d.target_calories != null) {
        result.target_calories += d.target_calories
        result.target_protein += d.target_protein_g || 0
        result.target_carbs += d.target_carbs_g || 0
        result.target_fat += d.target_fat_g || 0
      }
    })
    return result
  }, [days])

  const weekLabel = useMemo(() => {
    const start = new Date(startDate + 'T00:00:00')
    const end = new Date(endDate + 'T00:00:00')
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
  }, [startDate, endDate])

  const renderCell = (value, unit = '') => {
    if (value == null) return <span className="text-gray-300">—</span>
    return <span>{Math.round(value)}{unit && <span className="text-xs ml-0.5">{unit}</span>}</span>
  }

  const renderNetCalories = (value) => {
    if (value == null) return <span className="text-gray-300">—</span>
    const rounded = Math.round(value)
    // Positive net = surplus (consumed > burned) = bad
    // Negative net = deficit (burned > consumed) = good, show as positive with "+"
    if (rounded <= 0) return <span>+{Math.abs(rounded)}</span>
    return <span>-{rounded}</span>
  }

  const MACROS = [
    { key: 'calories', consumed: 'calories_consumed', target: 'target_calories', burned: 'calories_burned', label: t('overview.kcal', 'kcal'), color: 'text-orange-600', unit: '' },
    { key: 'protein', consumed: 'protein_consumed_g', target: 'target_protein_g', label: t('overview.protein', 'Protein'), color: 'text-green-600', unit: 'g' },
    { key: 'carbs', consumed: 'carbs_consumed_g', target: 'target_carbs_g', label: t('overview.carbs', 'Carbs'), color: 'text-blue-600', unit: 'g' },
    { key: 'fat', consumed: 'fat_consumed_g', target: 'target_fat_g', label: t('overview.fat', 'Fat'), color: 'text-amber-600', unit: 'g' },
  ]

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body animate-pulse"><div className="h-64 bg-gray-200 rounded"></div></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Week Navigator */}
      <div className="flex items-center justify-between">
        <button onClick={() => setWeekOffset((w) => w - 1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-semibold text-gray-900">{weekLabel}</h3>
        <button
          onClick={() => setWeekOffset((w) => Math.min(w + 1, 0))}
          disabled={weekOffset >= 0}
          className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-30"
        >
          <ChevronRightIcon className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Comparison Grid per Macro */}
      {MACROS.map((macro) => (
        <div key={macro.key} className="card overflow-hidden">
          <div className="card-header py-2 px-4">
            <h4 className={`text-sm font-semibold ${macro.color}`}>{macro.label}</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-24"></th>
                  {WEEKDAYS.map((day) => (
                    <th key={day} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      {t(`overview.weekdays.${day}`, day)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100">
                    {t('overview.total', 'Total')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Consumed Row */}
                <tr>
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-600">
                    {t('overview.consumed', 'Consumed')}
                  </td>
                  {WEEKDAYS.map((_, idx) => {
                    const d = dayMap[idx]
                    const consumedColor = macro.key === 'calories' ? 'text-red-500' : ''
                    return (
                      <td key={idx} className={`px-2 py-1.5 text-center text-xs ${consumedColor}`}>
                        {renderCell(d?.[macro.consumed])}
                      </td>
                    )
                  })}
                  <td className={`px-3 py-1.5 text-center text-xs font-semibold bg-gray-50 ${macro.key === 'calories' ? 'text-red-500' : ''}`}>
                    {renderCell(weekTotals[macro.consumed === 'calories_consumed' ? 'calories_consumed' : macro.key === 'protein' ? 'protein_consumed' : macro.key === 'carbs' ? 'carbs_consumed' : 'fat_consumed'])}
                  </td>
                </tr>

                {/* Target Row */}
                <tr>
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-600">
                    {t('overview.target', 'Target')}
                  </td>
                  {WEEKDAYS.map((_, idx) => {
                    const d = dayMap[idx]
                    return (
                      <td key={idx} className="px-2 py-1.5 text-center text-xs text-gray-400">
                        {renderCell(d?.[macro.target])}
                      </td>
                    )
                  })}
                  <td className="px-3 py-1.5 text-center text-xs font-semibold bg-gray-50 text-gray-400">
                    {renderCell(weekTotals[macro.target === 'target_calories' ? 'target_calories' : macro.key === 'protein' ? 'target_protein' : macro.key === 'carbs' ? 'target_carbs' : 'target_fat'])}
                  </td>
                </tr>

                {/* Burned Row (only for calories) */}
                {macro.burned && (
                  <tr>
                    <td className="px-3 py-1.5 text-xs font-medium text-gray-600">
                      {t('overview.burned', 'Burned')}
                    </td>
                    {WEEKDAYS.map((_, idx) => {
                      const d = dayMap[idx]
                      return (
                        <td key={idx} className="px-2 py-1.5 text-center text-xs text-green-600">
                          {renderCell(d?.[macro.burned])}
                        </td>
                      )
                    })}
                    <td className="px-3 py-1.5 text-center text-xs font-semibold bg-gray-50 text-green-600">
                      {renderCell(weekTotals.calories_burned)}
                    </td>
                  </tr>
                )}

                {/* Net/Difference Row (only for calories) */}
                {macro.burned && (
                  <tr className="bg-gray-50">
                    <td className="px-3 py-1.5 text-xs font-semibold text-gray-700">
                      {t('overview.net', 'Net')}
                    </td>
                    {WEEKDAYS.map((_, idx) => {
                      const d = dayMap[idx]
                      const statusClass = d?.status ? STATUS_COLORS[d.status] : ''
                      return (
                        <td key={idx} className={`px-2 py-1.5 text-center text-xs font-semibold ${statusClass}`}>
                          {renderNetCalories(d?.net_calories)}
                        </td>
                      )
                    })}
                    <td className="px-3 py-1.5 text-center text-xs font-bold bg-gray-100">
                      {renderNetCalories(weekTotals.calories_consumed - weekTotals.calories_burned)}
                    </td>
                  </tr>
                )}

                {/* Difference Row (for macros without burned) */}
                {!macro.burned && (
                  <tr className="bg-gray-50">
                    <td className="px-3 py-1.5 text-xs font-semibold text-gray-700">
                      {t('overview.diff', 'Diff')}
                    </td>
                    {WEEKDAYS.map((_, idx) => {
                      const d = dayMap[idx]
                      const consumed = d?.[macro.consumed]
                      const target = d?.[macro.target]
                      if (consumed == null || target == null) {
                        return <td key={idx} className="px-2 py-1.5 text-center text-xs text-gray-300">—</td>
                      }
                      const diff = consumed - target
                      const color = Math.abs(diff) < target * 0.1 ? 'text-green-600' : diff > 0 ? 'text-red-600' : 'text-amber-600'
                      return (
                        <td key={idx} className={`px-2 py-1.5 text-center text-xs font-semibold ${color}`}>
                          {diff > 0 ? '+' : ''}{Math.round(diff)}
                        </td>
                      )
                    })}
                    <td className="px-3 py-1.5 text-center text-xs font-bold bg-gray-100">
                      {(() => {
                        const totalKey = macro.key === 'protein' ? 'protein' : macro.key === 'carbs' ? 'carbs' : 'fat'
                        const diff = weekTotals[`${totalKey}_consumed`] - weekTotals[`target_${totalKey}`]
                        if (!weekTotals[`target_${totalKey}`]) return <span className="text-gray-300">—</span>
                        const color = Math.abs(diff) < weekTotals[`target_${totalKey}`] * 0.1 ? 'text-green-600' : diff > 0 ? 'text-red-600' : 'text-amber-600'
                        return <span className={color}>{diff > 0 ? '+' : ''}{Math.round(diff)}</span>
                      })()}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Weekly Summary */}
      {summary && (
        <div className="card">
          <div className="card-body">
            <h4 className="text-sm font-medium text-gray-500 mb-3">{t('overview.weeklySummary', 'Weekly Summary')}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-500">{t('overview.avgNet', 'Avg Net kcal')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {summary.avg_net_calories != null ? Math.round(summary.avg_net_calories) : '—'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">{t('overview.daysOnTarget', 'Days On Target')}</p>
                <p className="text-lg font-semibold text-green-600">{summary.days_on_target}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">{t('overview.daysSurplus', 'Days Surplus')}</p>
                <p className="text-lg font-semibold text-red-600">{summary.days_surplus}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">{t('overview.daysDeficit', 'Days Deficit')}</p>
                <p className="text-lg font-semibold text-amber-600">{summary.days_deficit}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
